import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Popover from '@mui/material/Popover';
import React from 'react';
import { APP_BAR } from './alliance-tokens';
import { BarOffsetContext, useIsMobileLayout } from './bar-controls';
import { CURRENT_APP } from './current-app';

/**
 * Surface colors for menus opened from the App Bar. Both DMD apps are light;
 * IDs and links use the app's menu accent (#2b5bd6, 5.9:1 on white).
 */
export const MENU_COLORS = {
  paper: '#ffffff',
  text: '#1b1c1d',
  text2: '#00000099',
  border: '#00000011',
  divider: '#0000001a',
  hover: '#0000000a',
  accent: CURRENT_APP.menuAccent,
};

/**
 * The surface for the App Launcher and the user menu.
 *
 * Desktop: a Popover 8px below the bar, right-aligned 16px from the window
 * edge (both menus line up there, whichever control opened them).
 * Below md: a bottom sheet with a drag handle and a scrim.
 *
 * @param open - Whether the menu is showing
 * @param onClose - Called on scrim click, Escape or swipe
 * @param width - Desktop width in px (user menu 340, launcher 360)
 * @param label - Accessible name for the menu surface
 */
export function BarMenu(props: {
  open: boolean;
  onClose: () => void;
  width: number;
  label: string;
  id: string;
  children: React.ReactNode;
}) {
  // Context
  const topOffset = React.useContext(BarOffsetContext);
  // Hooks
  const isMobile = useIsMobileLayout();

  if (isMobile) {
    return (
      <Drawer
        id={props.id}
        anchor="bottom"
        open={props.open}
        onClose={props.onClose}
        PaperProps={{
          'aria-label': props.label,
          sx: {
            borderRadius: `${APP_BAR.sheetRadius}px ${APP_BAR.sheetRadius}px 0 0`,
            maxHeight: '92%',
            // Clear the CookieYes consent button (44px, 16px from the
            // bottom-left) and the Freshchat button (56px, bottom-right)
            pb: '76px',
            color: MENU_COLORS.text,
            letterSpacing: 'normal',
            backgroundColor: MENU_COLORS.paper,
            backgroundImage: 'none',
          },
        }}
        slotProps={{ backdrop: { sx: { backgroundColor: '#00000080' } } }}
      >
        <Box
          sx={{
            width: 36,
            height: 4,
            borderRadius: '2px',
            backgroundColor: MENU_COLORS.divider,
            mx: 'auto',
            mt: '10px',
            mb: '2px',
            flex: 'none',
          }}
        />
        {props.children}
      </Drawer>
    );
  }

  return (
    <Popover
      id={props.id}
      open={props.open}
      onClose={props.onClose}
      anchorReference="anchorPosition"
      anchorPosition={{
        top: topOffset + APP_BAR.heightDesktop + APP_BAR.menuOffset,
        left: window.innerWidth - APP_BAR.paddingDesktop,
      }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        'aria-label': props.label,
        sx: {
          width: props.width,
          maxWidth: `calc(100vw - ${APP_BAR.paddingDesktop * 2}px)`,
          maxHeight: `calc(100% - ${
            topOffset + APP_BAR.heightDesktop + APP_BAR.menuOffset + 16
          }px)`,
          borderRadius: `${APP_BAR.menuRadius}px`,
          border: `1px solid ${MENU_COLORS.border}`,
          boxShadow: '0 12px 32px #0000003d',
          color: MENU_COLORS.text,
          letterSpacing: 'normal',
          backgroundColor: MENU_COLORS.paper,
          backgroundImage: 'none',
          overflowX: 'hidden',
        },
      }}
    >
      {props.children}
    </Popover>
  );
}

/** 11–12px uppercase section label inside a bar menu */
export function MenuOverline(props: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '.1em',
        textTransform: 'uppercase',
        color: MENU_COLORS.text2,
      }}
    >
      {props.children}
    </Box>
  );
}
